/**
 * Admin API: the price table (`/admin/products?view=prices`) — rows,
 * header counts, product and size prices, offering and withdrawing a
 * method on a product, and the bulk price action.
 *
 * Mirrors apps/api/src/admin/dto/admin-product-price-row.dto.ts and
 * admin-product-query.dto.ts. Money is a two-decimal string ("12.23");
 * an unknown amount is null, never 0.
 */
import axios from 'axios';
import apiClient, { ApiResponse } from '../client';
import type { ProductClass } from '@designdeck/shared';
import type { SalesPlatform } from './decoration-methods';
import type { AdminProductListResult } from './products';

/** Seller platforms with a channel markup (SellerPlatform in Prisma). */
export type SellerPlatform = 'shirtee' | 'justdroppit';

/** `none` prices without any markup (catalogue prices as they are). */
export type EkPlatform = SellerPlatform | 'none';

export const EK_PLATFORMS: EkPlatform[] = ['shirtee', 'justdroppit', 'none'];

/** Which price has to be missing for a product to match `missing=`. */
export type MissingPriceFilter =
  | 'purchasePrice'
  | 'recommendedSalesPrice'
  | 'decorationPrice';

/** Phoenix mapping filter: a Phoenix main product found, or none / never checked. */
export type PhoenixMappingFilter = 'mapped' | 'unmapped';

/** Mockup template filter: at least one template, or none at all. */
export type MockupTemplateFilter = 'present' | 'missing';

/**
 * Keys of the admin release channels (RELEASE_CHANNELS in
 * apps/api/src/products/channel-visibility.ts). Order and display names
 * come from the summary's `releaseChannels`, never from a copy here.
 */
export type ReleaseChannelKey = 'SHI' | 'SHICL' | 'JUSTDROPPIT';

/** One admin release channel as the table shows it as a column. */
export interface ReleaseChannel {
  key: ReleaseChannelKey;
  name: string;
}

/** Release per admin channel: released = active and listed there. */
export type ChannelReleaseStates = Record<ReleaseChannelKey, boolean>;

/**
 * Which Phoenix main product the product's variant SKUs belong to, as the
 * last `catalog:sync-phoenix-mapping` run saw it.
 */
export interface PhoenixMapping {
  /** null when Phoenix knows neither a variant SKU nor the article number. */
  productId: number | null;
  /** ACTIVE or INACTIVE at the check. */
  status: string | null;
  mappedVariants: number;
  comparedVariants: number;
  /** ISO timestamp. */
  checkedAt: string;
}

export type PriceStateKind =
  | 'product'
  | 'none'
  | 'uniform'
  | 'mixed'
  | 'placeholder';

export type SellerCostReason =
  | 'no_position'
  | 'blank_unknown'
  | 'no_method'
  | 'method_unpriced';

/**
 * One price column of a product: the product's own value when it has one
 * (`product`), otherwise what the sellable variants carry (`uniform`,
 * `mixed`, `placeholder`) or nothing at all (`none`).
 */
export interface PriceState {
  kind: PriceStateKind;
  /** Product value (kind product) or the uniform variant value. */
  value: string | null;
  /** Smallest variant value; null without variant values. */
  min: string | null;
  max: string | null;
  /** Sellable variants carrying their own value. */
  count: number;
}

export interface OfferedMethod {
  methodId: string;
  code: string;
  name: string;
  /** Offered on every active position of the product. */
  onAllPositions: boolean;
  /** Ids of active positions where this is the only offered method. */
  lastMethodOn: string[];
  /** A base price or a product override exists on the evaluated platform. */
  priced: boolean;
  overrides: Record<SalesPlatform, string | null>;
}

export interface LastPriceChange {
  /** ISO timestamp. */
  at: string;
  /** null when the entry has no user or the user was deleted. */
  userName: string | null;
}

export interface AdminProductPriceRow {
  id: string;
  name: string;
  artNumber: string | null;
  categoryId: string;
  manufacturerId: string | null;
  isActive: boolean;
  /** false for products without a size axis. */
  hasSizes: boolean;
  thumbnailImageUrl: string | null;
  /** Sellable variants of the product. */
  variantCount: number;
  purchasePrice: PriceState;
  recommendedSalesPrice: PriceState;
  /** What a seller of ekPlatform pays; null when unknown (see reason). */
  sellerCost: string | null;
  sellerCostReason: SellerCostReason | null;
  offeredMethods: OfferedMethod[];
  /** No offered method has a price on the evaluated platform. */
  decorationPriceMissing: boolean;
  lastPriceChange: LastPriceChange | null;
  /** null until the Phoenix mapping was checked. */
  phoenix: PhoenixMapping | null;
  /** ISO date the product was marked end of life (phased out, still
   * sold); null when unmarked. */
  endOfLifeAt: string | null;
  /** Carry-over class (TEXTILE, OBJECT); null = unknown, class rules off. */
  productClass: ProductClass | null;
  /** Carry-over percent of the product (1-100); null = default rules. */
  carryOverPercent: number | null;
  /** Release per admin channel; a channel without a row is false. */
  channels: ChannelReleaseStates;
}

/** Parameters a single row is evaluated with (same as the list). */
export interface PriceRowParams {
  ekPlatform?: EkPlatform;
  platform?: SalesPlatform;
}

export interface AdminProductPriceQuery extends PriceRowParams {
  search?: string;
  categoryId?: string;
  manufacturerId?: string;
  isActive?: boolean;
  /** Only products offering this method on an active position. */
  decorationMethodId?: string;
  /** `decorationPrice` requires `platform`. */
  missing?: MissingPriceFilter;
  phoenix?: PhoenixMappingFilter;
  mockup?: MockupTemplateFilter;
  /** Only products released on this channel (see `released`). */
  channel?: ReleaseChannelKey;
  /** Release state on `channel` (API default true); requires `channel`. */
  released?: boolean;
  page?: number;
  /** Up to 100; the API defaults to 50 for the price view. */
  limit?: number;
}

export type AdminPriceSummaryQuery = Omit<
  AdminProductPriceQuery,
  'page' | 'limit'
>;

export interface AdminProductPriceListResult {
  data: AdminProductPriceRow[];
  meta: AdminProductListResult['meta'];
}

export interface PlatformBasePrice {
  methodId: string;
  code: string;
  name: string;
  /** Base price on the platform; null when unpriced. */
  price: string | null;
}

/** Header numbers of the price table, counted over all matching products. */
export interface AdminProductPriceSummary {
  total: number;
  /** Active and inactive ignore the status filter. */
  active: number;
  inactive: number;
  missingPurchasePrice: number;
  /** Placeholders (999.99) count as missing. */
  missingRecommendedSalesPrice: number;
  /** null without a platform. */
  missingDecorationPrice: number | null;
  platform: SalesPlatform | null;
  basePrices: PlatformBasePrice[];
  /** The admin release channels in column order. */
  releaseChannels: ReleaseChannel[];
  /** Released products per channel; ignores the channel filter itself. */
  channels: Record<ReleaseChannelKey, number>;
}

/** Answer of `PATCH /admin/products/:id/channels/:channelKey`. */
export interface ChannelReleaseResult {
  productId: string;
  /** false when the channel already was in the requested state. */
  changed: boolean;
  channels: ChannelReleaseStates;
}

/** Body of `POST /admin/products/bulk-channel-release`. */
export interface BulkChannelReleaseInput {
  channelKey: ReleaseChannelKey;
  /** true releases, false withdraws. */
  released: boolean;
  /** Exactly one of productIds (1–500) or filter. */
  productIds?: string[];
  filter?: BulkPriceFilter;
}

/** Answer of `POST /admin/products/bulk-channel-release`. */
export interface BulkChannelReleaseResult {
  changed: number;
  unchanged: number;
  /** Inactive products of the selection; never touched. */
  skippedInactive: number;
}

/** Body of `PATCH /admin/products/:id/prices`; omitted fields stay. */
export interface SetProductPricesInput {
  purchasePrice?: number | null;
  recommendedSalesPrice?: number | null;
}

export interface SetProductPricesResult {
  product: {
    purchasePrice: string | null;
    recommendedSalesPrice: string | null;
  };
  /** Variant suggested prices cleared because a sales price was set. */
  clearedVariantPrices: number;
}

export type SizePriceKind = 'none' | 'uniform' | 'mixed';

/** Purchase price of one size, aggregated over its sellable variants. */
export interface SizePriceEntry {
  sizeId: string;
  name: string;
  kind: SizePriceKind;
  value: string | null;
  min: string | null;
  max: string | null;
  variantCount: number;
}

export interface SizePricesResult {
  /** false for products without a size axis. */
  hasSizes: boolean;
  sizes: SizePriceEntry[];
}

/** Answer of `PUT /admin/products/:id/decoration-methods/:methodId`. */
export interface OfferMethodResult {
  /** Active positions the method was added to; 0 when already everywhere. */
  positions: number;
}

/** A position whose default moved to another method after a withdrawal. */
export interface NewDefaultMethod {
  positionId: string;
  methodCode: string;
}

/** Answer of `DELETE /admin/products/:id/decoration-methods/:methodId`. */
export interface WithdrawMethodResult {
  /** Active positions the method was removed from. */
  positions: number;
  /** Design positions pinned to the withdrawn default (KD9). */
  pinnedDesignPositions: number;
  newDefaults: NewDefaultMethod[];
}

/** Error code of the 409 when the method is the last one on a position. */
export const LAST_METHOD_ON_POSITION = 'last_method_on_position';

/** True for the 409 the API answers when a position would lose its last method. */
export function isLastMethodConflict(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }
  const body = error.response.data as { code?: unknown } | undefined;
  return body?.code === LAST_METHOD_ON_POSITION;
}

export interface PositionLosingDefault {
  positionId: string;
  name: string;
  /** null when no method would remain to pick from. */
  newDefaultCode: string | null;
  embroideryBecomesDefault: boolean;
}

/** Answer of `GET /admin/products/:id/decoration-methods/:methodId/usage`. */
export interface MethodUsage {
  /** Active seller designs that chose the method explicitly. */
  explicit: number;
  /** Active seller designs using it as the position default. */
  implicit: number;
  positionsLosingDefault: PositionLosingDefault[];
  /** Active positions where this is the only method (withdraw blocked). */
  lastMethodOn: string[];
}

const DECORATION_COLUMN_PREFIX = 'decoration:';

/** `decoration:<methodId>` targets the override of one method. */
export type BulkPriceColumn =
  | 'purchasePrice'
  | 'recommendedSalesPrice'
  | `${typeof DECORATION_COLUMN_PREFIX}${string}`;

export function decorationColumn(methodId: string): BulkPriceColumn {
  return `${DECORATION_COLUMN_PREFIX}${methodId}`;
}

export function isDecorationColumn(column: BulkPriceColumn): boolean {
  return column.startsWith(DECORATION_COLUMN_PREFIX);
}

export type BulkPriceAction = 'set' | 'add' | 'percent' | 'reset';

/**
 * Target set by filter: the list filters without paging, plus the
 * selection mode flags. `all: true` targets every product.
 */
export interface BulkPriceFilter extends Omit<
  AdminProductPriceQuery,
  'page' | 'limit' | 'ekPlatform'
> {
  /** Rows deselected while "all filtered" is active. */
  excludedIds?: string[];
  all?: boolean;
}

/** Base value a product was previewed with; the real run skips changed ones. */
export interface BulkPriceExpected {
  productId: string;
  before: string | null;
}

export interface BulkPriceInput {
  dryRun: boolean;
  /** Exactly one of productIds (1–500) or filter. */
  productIds?: string[];
  filter?: BulkPriceFilter;
  column: BulkPriceColumn;
  /** Required for a decoration column. */
  platform?: SalesPlatform;
  action: BulkPriceAction;
  /** Required unless action is reset. Negative only for add/percent. */
  value?: number;
  /** From the preview; required for the real run. */
  expected?: BulkPriceExpected[];
}

export type BulkPriceSkipReason =
  | 'method_not_offered'
  | 'no_base_value'
  | 'no_base_price'
  | 'reset_not_applicable'
  | 'changed_since_preview'
  | 'unchanged';

export interface BulkPriceChange {
  productId: string;
  name: string;
  before: string | null;
  after: string | null;
  /** The result fell below 0 and was raised to 0.00. */
  clamped?: boolean;
}

export interface BulkPriceSkip {
  productId: string;
  name: string;
  reason: BulkPriceSkipReason;
}

export interface BulkPriceResult {
  changed: BulkPriceChange[];
  skipped: BulkPriceSkip[];
  clearedVariantPrices: number;
  expected: BulkPriceExpected[];
  /** Products the target set resolved to. */
  total: number;
  /** false for the preview, true after writing. */
  applied: boolean;
}

/** Query string of the price list, summary and single-row endpoints. */
function priceQueryParams(
  query: AdminProductPriceQuery,
  view: 'prices' | null
): URLSearchParams {
  const params = new URLSearchParams();
  if (view) params.append('view', view);
  if (query.ekPlatform) params.append('ekPlatform', query.ekPlatform);
  if (query.platform) params.append('platform', query.platform);
  if (query.search) params.append('search', query.search);
  if (query.categoryId) params.append('categoryId', query.categoryId);
  if (query.manufacturerId) {
    params.append('manufacturerId', query.manufacturerId);
  }
  if (query.isActive !== undefined) {
    params.append('isActive', String(query.isActive));
  }
  if (query.decorationMethodId) {
    params.append('decorationMethodId', query.decorationMethodId);
  }
  if (query.missing) params.append('missing', query.missing);
  if (query.phoenix) params.append('phoenix', query.phoenix);
  if (query.mockup) params.append('mockup', query.mockup);
  if (query.channel) {
    params.append('channel', query.channel);
    // The API refuses `released` without a channel.
    if (query.released !== undefined) {
      params.append('released', String(query.released));
    }
  }
  if (query.page) params.append('page', String(query.page));
  if (query.limit) params.append('limit', String(query.limit));
  return params;
}

/**
 * Price table rows: one per product with price state, offered methods,
 * seller cost for `ekPlatform` and the decoration flags for `platform`.
 */
export async function getAdminProductPriceRows(
  query: AdminProductPriceQuery = {}
): Promise<AdminProductPriceListResult> {
  const params = priceQueryParams(query, 'prices');
  const response = await apiClient.get<
    ApiResponse<AdminProductPriceListResult>
  >(`/admin/products?${params.toString()}`);
  return response.data.data;
}

/**
 * One price table row, evaluated with the same ekPlatform/platform as the
 * list so it can replace the cached row after a save.
 */
export async function getAdminProductPriceRow(
  productId: string,
  params: PriceRowParams = {}
): Promise<AdminProductPriceRow> {
  const search = priceQueryParams(params, null);
  const response = await apiClient.get<ApiResponse<AdminProductPriceRow>>(
    `/admin/products/${productId}/price-row?${search.toString()}`
  );
  return response.data.data;
}

/**
 * Header counts of the price table over all products matching the list
 * filters (`missing` is ignored by the API).
 */
export async function getAdminPriceSummary(
  query: AdminPriceSummaryQuery = {}
): Promise<AdminProductPriceSummary> {
  const params = priceQueryParams(query, null);
  const response = await apiClient.get<ApiResponse<AdminProductPriceSummary>>(
    `/admin/products/price-summary?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Set or clear the product-level purchase price and/or recommended sales
 * price. Only the sent fields change; null clears. A set sales price
 * clears the suggested prices of the product's variants (count returned).
 */
export async function setProductPrices(
  productId: string,
  input: SetProductPricesInput
): Promise<SetProductPricesResult> {
  const response = await apiClient.patch<ApiResponse<SetProductPricesResult>>(
    `/admin/products/${productId}/prices`,
    input
  );
  return response.data.data;
}

/**
 * Purchase price per size, aggregated over the sellable variants.
 */
export async function getSizePrices(
  productId: string
): Promise<SizePricesResult> {
  const response = await apiClient.get<ApiResponse<SizePricesResult>>(
    `/admin/products/${productId}/size-prices`
  );
  return response.data.data;
}

/**
 * Set or clear the purchase price of every sellable variant of one size.
 * null clears; 0 is a real price.
 */
export async function setSizePrice(
  productId: string,
  sizeId: string,
  purchasePrice: number | null
): Promise<SizePriceEntry> {
  const response = await apiClient.put<ApiResponse<SizePriceEntry>>(
    `/admin/products/${productId}/size-prices/${sizeId}`,
    { purchasePrice }
  );
  return response.data.data;
}

/**
 * Offer a decoration method on every active position of a product.
 * Idempotent: positions that already offer it are left alone.
 */
export async function offerProductMethod(
  productId: string,
  methodId: string
): Promise<OfferMethodResult> {
  const response = await apiClient.put<ApiResponse<OfferMethodResult>>(
    `/admin/products/${productId}/decoration-methods/${methodId}`
  );
  return response.data.data;
}

/**
 * Withdraw a decoration method from every active position of a product.
 * Designs using it as the position default are pinned to it; the next
 * method becomes the default. 409 when it is the last method somewhere.
 */
export async function withdrawProductMethod(
  productId: string,
  methodId: string
): Promise<WithdrawMethodResult> {
  const response = await apiClient.delete<ApiResponse<WithdrawMethodResult>>(
    `/admin/products/${productId}/decoration-methods/${methodId}`
  );
  return response.data.data;
}

/**
 * How many active seller designs use a method on the product and which
 * positions would change their default if it were withdrawn.
 */
export async function getProductMethodUsage(
  productId: string,
  methodId: string
): Promise<MethodUsage> {
  const response = await apiClient.get<ApiResponse<MethodUsage>>(
    `/admin/products/${productId}/decoration-methods/${methodId}/usage`
  );
  return response.data.data;
}

/** Longer than the API's 60 s price-write transaction timeout. */
const BULK_PRICE_RUN_TIMEOUT_MS = 70_000;

/**
 * Change one price column of many products in one transaction. With
 * `dryRun` the API only plans and returns the preview (plus `expected`,
 * which the real run sends back to skip products changed meanwhile).
 */
export async function bulkPrice(
  input: BulkPriceInput
): Promise<BulkPriceResult> {
  const response = await apiClient.post<ApiResponse<BulkPriceResult>>(
    '/admin/products/bulk-price',
    input,
    // A real run over up to 1000 products may run close to the server's
    // 60 s transaction timeout; the default 30 s client timeout would give
    // up on a transaction that is still going to commit.
    input.dryRun ? undefined : { timeout: BULK_PRICE_RUN_TIMEOUT_MS }
  );
  return response.data.data;
}

/**
 * Release (true) or withdraw (false) one product on one admin channel.
 * Writes isActive and isListed of the release row together.
 */
export async function setProductChannelRelease(
  productId: string,
  channelKey: ReleaseChannelKey,
  released: boolean
): Promise<ChannelReleaseResult> {
  const response = await apiClient.patch<ApiResponse<ChannelReleaseResult>>(
    `/admin/products/${productId}/channels/${channelKey}`,
    { released }
  );
  return response.data.data;
}

/**
 * Release or withdraw one admin channel for many products in one
 * transaction; inactive products are skipped.
 */
export async function bulkChannelRelease(
  input: BulkChannelReleaseInput
): Promise<BulkChannelReleaseResult> {
  const response = await apiClient.post<ApiResponse<BulkChannelReleaseResult>>(
    '/admin/products/bulk-channel-release',
    input,
    { timeout: BULK_PRICE_RUN_TIMEOUT_MS }
  );
  return response.data.data;
}
